import { Injectable } from '@angular/core';
import { LocalNotificationSchema, LocalNotifications, PermissionStatus } from '@capacitor/local-notifications';
import { NotificationService } from './notification.service';

import dayjs from '../../_utils/dayjs.config';
import { FeedbackService } from '../feedback.service';
import { TimeTicketBundle, ZeitenService } from '../zeiten.service';
import { TimeOverviewBasePage } from '../../_template/time-overview/time-overview-base/time-overview-base.page';
import { TicketZeit } from '../../_interfaces/TicketTime';
import { AuthService } from '../auth.service';
import { Platform } from '@ionic/angular';
import OneSignal from 'onesignal-cordova-plugin';

import WorkTimeLiveAcivity from '../../_native/worktimeLiveActivity';
import { StorageManagerService } from '../storage-manager.service';

@Injectable({
  providedIn: 'root'
})
export class LocalNotificationService {

  constructor(
    private notificationSrv: NotificationService,
    private feedback: FeedbackService,
    private authSrv: AuthService,
    private platform: Platform,
    private storageManager: StorageManagerService
  ) { 
    LocalNotifications.addListener('localNotificationActionPerformed', async (notification) => {
      if(notification.notification.ongoing == true) {
        await this.feedback.openModal(TimeOverviewBasePage)
        setTimeout(async () => await this.shedule([notification.notification]), 1000);
      }
    })
  }
 
  /**
   * Sendet eine lokale Benachrichtigung, die anzeigt, dass die Arbeitszeit für ein Ticket läuft.
   * @param name - Der Name des Tickets.
   * @param id - Die ID des Tickets.
   * @param timeFrom - Das Datum und die Uhrzeit, zu der die Arbeitszeit gestartet wurde.
   */
  public async sendWorkingTimeNotification(name: string, id: number, timeFrom: Date){
    if(!this.platform.is("capacitor") || !this.notificationSrv.workingTimes) return

    if(this.platform.is("android")){
      const time = dayjs(timeFrom).format("HH:mm")
      const localNotification: LocalNotificationSchema = {
        extra: {
          workingTime: true,
        },
        title: "Laufende Zeit",
        body: `Ticketzeit von "${name}" läuft seit ${time} Uhr.`,
        id: id,
        ongoing: true,
      }
  
      await this.shedule([localNotification])
    }else if(this.platform.is("ios")){
      const companyIdentifier: string = await this.storageManager.getItem<string>("COMPANY_IDENTIFIER", "NICHTS")
      const token: string = [10, 15, 25, 5].map(num => num.toString(16).padStart(2, '0')).join('');

      try {
        await WorkTimeLiveAcivity.start({
            name: name,
            time: Math.floor(dayjs().diff(dayjs(timeFrom)) / 1000) * -1,
            id: `${companyIdentifier}-${id.toString()}` ,
            token: token,
            active: true
          })
        
      } catch (error) {
        alert(error?.message || "Ein Fehler ist aufgetreten beim Starten der Live-Aktivität.")
      }
    }

  }

  /**
   * Stoppt die Benachrichtigung für die Arbeitszeit.
   * 
   * @param id - Die ID der Benachrichtigung, die gestoppt werden soll.
   * @returns Ein Promise, das aufgelöst wird, wenn die Benachrichtigung erfolgreich gestoppt wurde.
   */
  public async stopWorkingTimeNotification(id: number){
    await LocalNotifications.cancel({notifications: [{id: id}]})
  }

  /**
   * Ruft alle ausstehenden Benachrichtigungen ab und bricht sie ab.
   * @returns Eine Promise, die gelöst wird, wenn alle Benachrichtigungen erfolgreich abgebrochen wurden.
   */
  public async cancelAllNotifications(){
    const notifications = (await LocalNotifications.getDeliveredNotifications()).notifications
    await LocalNotifications.cancel({notifications: notifications})
  }

  /**
   * Überprüft, ob es Benachrichtigungen gibt, die noch aktiv sind und aktualisiert sie gegebenenfalls.
   * @param timeBundle - Ein Array von `TimeTicketBundle` Objekten, die die Arbeitszeiten und Tickets enthalten.
   * @returns Eine Promise, die ein Array von `LocalNotification` Objekten enthält.
   */
  public async checkOngoingNotifications(timeBundle: TimeTicketBundle[]){
    if(!this.notificationSrv.workingTimes) return
    const notifications = (await LocalNotifications.getDeliveredNotifications()).notifications
    const timeIds = timeBundle.filter(bundle => bundle.time.employeeId == this.authSrv.getUser().id).map(bundle => bundle.time.id)

    notifications.forEach(notification => {
      if(!timeIds.includes(notification.id)) LocalNotifications.cancel({notifications: [{id: notification.id}]})
    })

    timeIds.forEach(id => {
      if(notifications.find(notification => notification.id == id)) return
      this.sendWorkingTimeNotification(timeBundle.find(time => time.time.id == id).ticket.description, id, timeBundle.find(time => time.time.id == id).time.timeFrom)
    })
  }

  /**
   * Plant eine oder mehrere lokale Benachrichtigungen.
   * 
   * @param data - Ein Array von `LocalNotificationSchema` Objekten, die die Benachrichtigungen darstellen.
   * @returns Eine Promise, die bei erfolgreicher Ausführung zurückgegeben wird.
   */
  public async shedule(data: LocalNotificationSchema[]){
    const permission: PermissionStatus = await LocalNotifications.requestPermissions();
    if(permission.display != 'granted') {
      const requestResult: PermissionStatus = await LocalNotifications.requestPermissions();
      if(requestResult.display != 'granted') return
    }
    await LocalNotifications.schedule({
      notifications: data
    });
  } 
} 
